import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Save, Users2, Star, Wallet, Activity, BadgeCheck, Clock, CalendarX } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, CardHead, Btn, Badge, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";
import Packages from "@/pages/admin/Packages";
import Directory from "@/pages/admin/Directory";
import Users from "@/pages/admin/Users";
import AdminPatients from "@/pages/admin/AdminPatients";
import AdminSessions from "@/pages/admin/AdminSessions";
import AdminLegal from "@/pages/admin/AdminLegal";
import { useAuth } from "@/context/AuthContext";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { DashboardCustomizer } from "@/components/DashboardCustomizer";

const OPS_WIDGETS = [
  { id: "kpis", label: "Key metrics" },
  { id: "config", label: "Treatment package config" },
  { id: "credentialing", label: "Credentialing shortcuts" },
  { id: "expiry", label: "Credential expiry alerts" },
  { id: "rate_master", label: "Rate master pricing" },
  { id: "rate_simulator", label: "Rate simulator" },
  { id: "case_assignment", label: "Case assignment" },
];

export default function Admin() {
  const [cfg, setCfg] = useState(null);
  const [physios, setPhysios] = useState([]);
  const [ov, setOv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("ops");
  const isSuper = useAuth().user?.role === "superadmin";
  const [sim, setSim] = useState({ rehab: "Orthopedic", sessions: 10, tier: "Standard", loc: "Tier 1", home: false, equip: false });
  const opsLayout = useDashboardLayout("admin-ops", OPS_WIDGETS);

  const loadPhysios = useCallback(() => api.get("/admin/physios").then((r) => setPhysios(r.data)), []);
  const loadOv = useCallback(() => api.get("/admin/overview").then((r) => setOv(r.data)), []);
  const [expiring, setExpiring] = useState([]);
  const loadExpiring = useCallback(() => api.get("/admin/expiring-credentials").then((r) => setExpiring(r.data)), []);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); loadPhysios(); loadOv(); loadExpiring(); }, [loadPhysios, loadOv, loadExpiring]);
  if (!cfg) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const money = (n) => `${cfg.currency}${Number(n || 0).toLocaleString("en-IN")}`;

  const save = async () => {
    if (false) return;
    setSaving(true);
    try {
      const { data } = await api.put("/config", {
        package_amount: Number(cfg.package_amount), package_name: cfg.package_name,
        split_physio: Number(cfg.split_physio), split_platform: Number(cfg.split_platform),
        split_doctor: Number(cfg.split_doctor), nmc_regulation: cfg.nmc_regulation,
        per_session: Number(cfg.per_session), equipment_fee: Number(cfg.equipment_fee),
        home_visit_fee: Number(cfg.home_visit_fee), base_rates: cfg.base_rates,
        clinician_multiplier: cfg.clinician_multiplier, location_multiplier: cfg.location_multiplier,
      });
      setCfg(data); toast.success("Config updated — applies to all new orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const reassign = async (orderId, physioId) => {
    if (!physioId) return;
    await api.post(`/admin/orders/${orderId}/reassign`, { physio_id: physioId });
    toast.success("Therapist reassigned"); loadOv();
  };
  const setRate = (type) => (e) => setCfg({ ...cfg, base_rates: { ...cfg.base_rates, [type]: Number(e.target.value) } });
  const runReminders = async () => {
    try { const { data } = await api.post("/admin/credential-reminders/run"); toast.success(`${data.sent} renewal reminder${data.sent !== 1 ? "s" : ""} sent`); }
    catch (e) { toast.error("Could not send reminders"); }
  };
  const simAmount = () => {
    const base = (cfg.base_rates || {})[sim.rehab] || 0;
    const sc = (cfg.per_session || 0) * Number(sim.sessions || 0);
    const cm = (cfg.clinician_multiplier || {})[sim.tier] || 1;
    const lm = (cfg.location_multiplier || {})[sim.loc] || 1;
    const add = (sim.home ? (cfg.home_visit_fee || 0) : 0) + (sim.equip ? (cfg.equipment_fee || 0) : 0);
    return Math.round((base + sc) * cm * lm + add);
  };

  const KPI = ov ? [
    { label: "Total Orders", value: ov.total_orders, icon: Activity, tone: "bg-sky-50 text-sky-600" },
    { label: "Gross Volume", value: money(ov.gross_volume), icon: Wallet, tone: "bg-teal-50 text-teal-600", small: true },
    { label: "Expiring Credentials", value: ov.expiring_credentials, icon: BadgeCheck, tone: "bg-violet-50 text-violet-600" },
    { label: "Physios Pending", value: ov.pending_physios, icon: Clock, tone: "bg-amber-50 text-amber-600" },
  ] : [];

  const OPS_W = {
    kpis: ov ? (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI.map((s) => (
          <Card key={s.label} hover className="p-5 flex gap-3.5 items-start" data-testid={`admin-kpi-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tone}`}><s.icon size={20} /></div>
            <div><div className={`metric font-extrabold text-slate-900 leading-none ${s.small ? "text-lg" : "text-2xl"}`}>{s.value}</div><div className="text-[12.5px] text-slate-500 mt-1">{s.label}</div></div>
          </Card>
        ))}
      </div>
    ) : null,
    config: (
      <Card className="p-6">
        <h3 className="font-display font-semibold mb-4">Treatment Package</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Package name"><input className="pq-input" value={cfg.package_name} onChange={set("package_name")} data-testid="admin-package-name" /></Field>
          <Field label={`Package amount (${cfg.currency})`}><input type="number" className="pq-input metric" value={cfg.package_amount} onChange={set("package_amount")} data-testid="admin-package-amount" /></Field>
        </div>
        <label className="mt-4 flex items-center gap-2.5 bg-slate-50 rounded-lg px-3 py-2.5 text-[12.5px] font-medium cursor-pointer" data-testid="require-doctor-verification-toggle">
          <input type="checkbox" className="accent-teal-600 w-4 h-4" checked={!!cfg.require_doctor_verification} onChange={(e) => setCfg({ ...cfg, require_doctor_verification: e.target.checked })} data-testid="require-doctor-verification-input" />
          <span>Require doctors to be verified before submitting treatment orders</span>
        </label>
        <div className="mt-3 flex gap-2 items-start bg-amber-50 text-amber-700 rounded-lg p-2.5 text-[11.5px]"><ShieldCheck size={14} className="shrink-0 mt-0.5" /><span>Monitored for NMC Reg {cfg.nmc_regulation} compliance. Last updated by {cfg.updated_by || "PranIQ Admin"}.</span></div>
        <Btn variant="primary" className="mt-4" onClick={save} disabled={saving} data-testid="admin-save-config">{saving ? <Spinner className="w-4 h-4" /> : <><Save size={15} /> Save & apply live</>}</Btn>
      </Card>
    ),
    credentialing: (
      <Card className="p-6">
        <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Users2 size={17} className="text-teal-600" /> Credentialing</h3>
        <p className="text-[13px] text-slate-500 mb-4">Manage clinician & doctor documents, photos and verification in the dedicated directories.</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setTab("clinicians")} className="bg-slate-50 hover:bg-teal-50 transition-colors rounded-xl py-4 text-center" data-testid="ops-goto-clinicians">
            <div className="metric text-2xl font-extrabold text-teal-700">{physios.length}</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Clinicians →</div>
          </button>
          <button onClick={() => setTab("doctors")} className="bg-slate-50 hover:bg-teal-50 transition-colors rounded-xl py-4 text-center" data-testid="ops-goto-doctors">
            <div className="metric text-2xl font-extrabold text-teal-700">{ov?.roles?.doctor || 0}</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Doctors →</div>
          </button>
        </div>
        <button onClick={() => setTab("packages")} className="w-full mt-3 bg-slate-50 hover:bg-teal-50 transition-colors rounded-xl py-3 text-center text-[13px] font-semibold text-slate-700" data-testid="ops-goto-packages">
          Manage Treatment Packages →
        </button>
      </Card>
    ),
    expiry: (
      <Card data-testid="expiring-credentials-card">
        <CardHead title={<span className="flex items-center gap-2"><CalendarX size={17} className="text-amber-500" /> Credential Expiry Alerts</span>}
          action={<div className="flex items-center gap-2"><Badge tone={expiring.length ? "amber" : "slate"}>{expiring.length} within 60 days</Badge><Btn size="sm" onClick={runReminders} data-testid="send-reminders-btn"><Save size={13} /> Send reminders now</Btn></div>} />
        {expiring.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No credentials expiring in the next 60 days.</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Clinician / Doctor", "Role", "Document", "Expiry", "Status"].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
            <tbody>
              {expiring.map((e) => (
                <tr key={e.document_id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`expiring-${e.document_id}`}>
                  <td className="px-4 py-3 font-semibold">{e.user_name}</td>
                  <td className="px-4 py-3 text-slate-500 capitalize">{e.role === "physiotherapist" ? "Clinician" : e.role}</td>
                  <td className="px-4 py-3 capitalize">{e.doc_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 metric">{e.expiry}</td>
                  <td className="px-4 py-3">
                    <Badge tone={e.expired ? "rose" : e.days_left <= 14 ? "amber" : "slate"}>
                      {e.expired ? `Expired ${Math.abs(e.days_left)}d ago` : `${e.days_left}d left`}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
    ),
    rate_master: (ov && ov.recent_orders?.length > 0) ? (
      <Card>
        <CardHead title="Rate Master — Dynamic Pricing" />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label={`Per-session (${cfg.currency})`}><input type="number" className="pq-input metric" value={cfg.per_session || 0} onChange={set("per_session")} data-testid="rate-per-session" /></Field>
          <Field label={`Equipment fee (${cfg.currency})`}><input type="number" className="pq-input metric" value={cfg.equipment_fee || 0} onChange={set("equipment_fee")} data-testid="rate-equipment" /></Field>
          <Field label={`Home-visit fee (${cfg.currency})`}><input type="number" className="pq-input metric" value={cfg.home_visit_fee || 0} onChange={set("home_visit_fee")} data-testid="rate-home" /></Field>
        </div>
        <div className="px-5 pb-4">
          <div className="pq-label mb-2">Base rate by rehab type</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(cfg.base_rates || {}).map(([t, v]) => (
              <Field key={t} label={t}><input type="number" className="pq-input metric" value={v} onChange={setRate(t)} data-testid={`rate-base-${t}`} /></Field>
            ))}
          </div>
          <p className="text-[11.5px] text-slate-500 mt-3">Order price = (base + per-session × sessions) × clinician tier × location tier + equipment/home-visit fees. Clinician: Standard 1.0 / Senior 1.3 / Specialist 1.6 · Location: Metro 1.2 / Tier 1 1.0 / Tier 2 0.9.</p>
          <Btn variant="primary" className="mt-3" onClick={save} disabled={saving} data-testid="rate-save"><Save size={15} /> Save rate master</Btn>
        </div>
      </Card>
    ) : null,
    rate_simulator: (
      <Card data-testid="rate-simulator">
        <CardHead title="Rate Simulator — what-if pricing" />
        <div className="p-5 flex flex-wrap items-end gap-3">
          <Field label="Rehab type"><select className="pq-input py-1.5" value={sim.rehab} onChange={(e) => setSim({ ...sim, rehab: e.target.value })} data-testid="sim-rehab">{Object.keys(cfg.base_rates || {}).map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Sessions"><input type="number" className="pq-input py-1.5 max-w-[90px] metric" value={sim.sessions} onChange={(e) => setSim({ ...sim, sessions: e.target.value })} data-testid="sim-sessions" /></Field>
          <Field label="Clinician"><select className="pq-input py-1.5" value={sim.tier} onChange={(e) => setSim({ ...sim, tier: e.target.value })} data-testid="sim-tier">{Object.keys(cfg.clinician_multiplier || {}).map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Location"><select className="pq-input py-1.5" value={sim.loc} onChange={(e) => setSim({ ...sim, loc: e.target.value })} data-testid="sim-loc">{Object.keys(cfg.location_multiplier || {}).map((t) => <option key={t}>{t}</option>)}</select></Field>
          <label className="flex items-center gap-1.5 text-[13px] font-medium mb-2"><input type="checkbox" className="accent-teal-600" checked={sim.home} onChange={(e) => setSim({ ...sim, home: e.target.checked })} /> Home visit</label>
          <label className="flex items-center gap-1.5 text-[13px] font-medium mb-2"><input type="checkbox" className="accent-teal-600" checked={sim.equip} onChange={(e) => setSim({ ...sim, equip: e.target.checked })} /> Equipment</label>
          <div className="ml-auto text-right"><div className="pq-label">Order price</div><div className="metric text-2xl font-extrabold text-teal-700" data-testid="sim-amount">{money(simAmount())}</div></div>
        </div>
      </Card>
    ),
    case_assignment: (ov && ov.recent_orders?.length > 0) ? (
      <Card>
        <CardHead title="Case Assignment — Reassign Therapists" />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Order", "Patient", "Current Therapist", "Amount", "Reassign to"].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {ov.recent_orders.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 last:border-0" data-testid={`assign-row-${o.id}`}>
                <td className="px-4 py-3 font-semibold text-[13px]">{o.ref_no}</td>
                <td className="px-4 py-3 text-[13px]">{o.patient_name}</td>
                <td className="px-4 py-3 text-[13px]">{o.assigned_physio?.name || "—"}</td>
                <td className="px-4 py-3 metric text-[13px]">{money(o.billing?.amount)}</td>
                <td className="px-4 py-3">
                  <select className="pq-input py-1.5 max-w-[220px]" defaultValue="" onChange={(e) => reassign(o.id, e.target.value)} data-testid={`reassign-${o.id}`}>
                    <option value="" disabled>Select therapist…</option>
                    {physios.filter((p) => p.credential_status === "Verified" && p.active !== false && !p.account_suspended).map((p) => <option key={p.id} value={p.id}>{`${p.name} (${p.tier || "Standard"})`}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
    ) : null,
  };

  return (
    <>
      <PageHead title="Platform Operations" subtitle="Configuration, credentialing, billing & finance."
        action={<div className="flex gap-2 flex-wrap">
          <button className={`pq-chip ${tab === "ops" ? "pq-chip-active" : ""}`} onClick={() => setTab("ops")} data-testid="admin-tab-ops">Operations</button>
          <button className={`pq-chip ${tab === "users" ? "pq-chip-active" : ""}`} onClick={() => setTab("users")} data-testid="admin-tab-users">Users</button>
          <button className={`pq-chip ${tab === "packages" ? "pq-chip-active" : ""}`} onClick={() => setTab("packages")} data-testid="admin-tab-packages">Packages</button>
          <button className={`pq-chip ${tab === "rates" ? "pq-chip-active" : ""}`} onClick={() => setTab("rates")} data-testid="admin-tab-rates">Rate Master</button>
          <button className={`pq-chip ${tab === "clinicians" ? "pq-chip-active" : ""}`} onClick={() => setTab("clinicians")} data-testid="admin-tab-clinicians">Clinicians</button>
          <button className={`pq-chip ${tab === "sessions" ? "pq-chip-active" : ""}`} onClick={() => setTab("sessions")} data-testid="admin-tab-sessions">Therapist Sessions</button>
          <button className={`pq-chip ${tab === "doctors" ? "pq-chip-active" : ""}`} onClick={() => setTab("doctors")} data-testid="admin-tab-doctors">Doctors</button>
          <button className={`pq-chip ${tab === "patients" ? "pq-chip-active" : ""}`} onClick={() => setTab("patients")} data-testid="admin-tab-patients">Patients</button>
          <button className={`pq-chip ${tab === "billing" ? "pq-chip-active" : ""}`} onClick={() => setTab("billing")} data-testid="admin-tab-billing">Billing & Finance</button>
          {isSuper && <button className={`pq-chip ${tab === "legal" ? "pq-chip-active" : ""}`} onClick={() => setTab("legal")} data-testid="admin-tab-legal">Legal documents</button>}
        </div>} />

      {tab === "ops" && (<>
        <div className="flex justify-end mb-4"><DashboardCustomizer {...opsLayout} /></div>
        <div className="space-y-5">
          {opsLayout.items.filter((i) => i.visible).map((i) => (
            OPS_W[i.id] ? <div key={i.id} data-testid={`admin-dash-widget-${i.id}`}>{OPS_W[i.id]}</div> : null
          ))}
        </div>
      </>)}

      {tab === "packages" && <Packages currency={cfg.currency} />}
      {tab === "rates" && <RateMaster currency={cfg.currency} />}
      {tab === "users" && <Users />}
      {tab === "clinicians" && <Directory role="physiotherapist" />}
      {tab === "sessions" && <AdminSessions />}
      {tab === "legal" && isSuper && <AdminLegal />}
      {tab === "doctors" && <Directory role="doctor" />}
      {tab === "patients" && <AdminPatients />}
      {tab === "billing" && <BillingView />}
    </>
  );
}

function RateMasterSub({ title, children }) {
  return <div className="mb-5"><div className="pq-label mb-2">{title}</div>{children}</div>;
}

function RateMaster({ currency }) {
  const [rm, setRm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/rate-master").then((r) => setRm({ ...r.data, equipment: (r.data.equipment || []).map((e, i) => ({ _k: `eq-${i}-${Date.now()}`, ...e })) })); }, []);
  if (!rm) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>;
  const num = (v) => Number(v) || 0;
  const setTop = (k) => (e) => setRm({ ...rm, [k]: num(e.target.value) });
  const setMap = (grp, key) => (e) => setRm({ ...rm, [grp]: { ...rm[grp], [key]: Number(e.target.value) } });
  const setEq = (i, k) => (e) => { const eq = [...rm.equipment]; eq[i] = { ...eq[i], [k]: e.target.value }; setRm({ ...rm, equipment: eq }); };
  const addEq = () => setRm({ ...rm, equipment: [...(rm.equipment || []), { _k: `eq-new-${Date.now()}`, name: "New equipment", fee: "" }] });
  const delEq = (i) => setRm({ ...rm, equipment: rm.equipment.filter((_, j) => j !== i) });
  const save = async () => {
    setSaving(true);
    const payload = { ...rm, equipment: (rm.equipment || []).map(({ _k, ...e }) => ({ ...e, fee: num(e.fee) })) };
    try { await api.put("/admin/rate-master", payload); toast.success("Rate Master saved — applies to all doctors"); }
    catch { toast.error("Could not save rate master"); }
    setSaving(false);
  };
  const Sub = RateMasterSub;
  return (
    <Card className="p-5" data-testid="rate-master-editor">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-display font-semibold text-[15px]">Rate Master</h3><p className="text-[12.5px] text-slate-500">Global pricing applied to every doctor's treatment orders. Floor is enforced per session.</p></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Field label={`Base per-session (${currency})`}><input type="number" className="pq-input metric" value={rm.base_per_session} onChange={setTop("base_per_session")} data-testid="rm-base" /></Field>
        <Field label={`Minimum per-session (${currency})`}><input type="number" className="pq-input metric" value={rm.min_per_session} onChange={setTop("min_per_session")} data-testid="rm-min" /></Field>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5">
        <Sub title="City / town tier multiplier">
          {Object.entries(rm.city_tiers || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 mb-2"><span className="text-[12.5px] w-24 shrink-0">{k}</span><input type="number" step="0.05" className="pq-input py-1.5 metric w-full" value={v} onChange={setMap("city_tiers", k)} data-testid={`rm-city-${k.replace(/\s/g,'')}`} /></div>
          ))}
        </Sub>
        <Sub title="Severity multiplier">
          {Object.entries(rm.severity_multiplier || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 mb-2"><span className="text-[12.5px] w-24 shrink-0">{k}</span><input type="number" step="0.05" className="pq-input py-1.5 metric w-full" value={v} onChange={setMap("severity_multiplier", k)} data-testid={`rm-sev-${k}`} /></div>
          ))}
        </Sub>
        <Sub title="Treatment-type multiplier">
          {Object.entries(rm.treatment_multiplier || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 mb-2"><span className="text-[12.5px] w-24 shrink-0">{k}</span><input type="number" step="0.05" className="pq-input py-1.5 metric w-full" value={v} onChange={setMap("treatment_multiplier", k)} data-testid={`rm-treat-${k}`} /></div>
          ))}
        </Sub>
      </div>
      <Sub title="Special equipment — additional charges">
        <div className="space-y-2" data-testid="rm-equipment-list">
          {(rm.equipment || []).map((eq, i) => (
            <div key={eq._k || i} className="flex items-center gap-2">
              <input className="pq-input py-1.5 flex-1" value={eq.name} onChange={setEq(i, "name")} data-testid={`rm-eq-name-${i}`} />
              <span className="text-slate-400 text-[13px]">{currency}</span>
              <input type="number" className="pq-input py-1.5 max-w-[110px] metric" value={eq.fee} onChange={setEq(i, "fee")} data-testid={`rm-eq-fee-${i}`} />
              <button onClick={() => delEq(i)} className="text-slate-400 hover:text-rose-500 text-[12px] font-semibold px-2" data-testid={`rm-eq-del-${i}`}>Remove</button>
            </div>
          ))}
        </div>
        <button onClick={addEq} className="mt-2 text-[12.5px] font-semibold text-teal-700 hover:underline" data-testid="rm-eq-add">+ Add equipment</button>
      </Sub>
      <p className="text-[11.5px] text-slate-500">Per-session = max(minimum, base × city tier × severity × treatment). Order total = per-session × sessions + selected equipment charges.</p>
      <div className="flex justify-end mt-4 pt-4 border-t border-slate-100">
        <Btn variant="primary" onClick={save} disabled={saving} data-testid="rate-master-save">{saving ? <Spinner className="w-4 h-4" /> : <><Save size={15} /> Save & apply</>}</Btn>
      </div>
    </Card>
  );
}

function BillingView() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/billing").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>;
  const money = (n) => `${d.currency}${Number(n || 0).toLocaleString("en-IN")}`;
  const t = d.totals;
  const exportCsv = async () => {
    const res = await api.get("/admin/billing/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "praniq_payouts.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    const res = await api.get("/admin/billing/export.pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "praniq_statement.pdf"; a.click(); URL.revokeObjectURL(url);
  };
  const setBilling = async (id, body, msg) => {
    try { await api.put(`/admin/billing/${id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const delTxn = async (o) => {
    if (!window.confirm(`Permanently delete order ${o.ref_no} (${o.patient_name})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/orders/${o.id}`); toast.success("Transaction deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const CARDS = [
    { label: "Gross Volume", value: money(t.gross), icon: Wallet, tone: "bg-teal-50 text-teal-600" },
    { label: "Collected", value: money(t.collected), icon: BadgeCheck, tone: "bg-emerald-50 text-emerald-600" },
    { label: "Disbursed", value: money(t.disbursed), icon: Activity, tone: "bg-violet-50 text-violet-600" },
    { label: "Pending Payouts", value: money(t.pending_payout), icon: Clock, tone: "bg-amber-50 text-amber-600" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {CARDS.map((s) => (
          <Card key={s.label} hover className="p-5 flex gap-3.5 items-start" data-testid={`billing-kpi-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tone}`}><s.icon size={20} /></div>
            <div><div className="metric font-extrabold text-lg text-slate-900 leading-none">{s.value}</div><div className="text-[12.5px] text-slate-500 mt-1">{s.label}</div></div>
          </Card>
        ))}
      </div>
      <Card className="mb-5">
        <CardHead title="Monthly Reconciliation" action={<div className="flex gap-2"><Btn size="sm" onClick={exportCsv} data-testid="billing-export-csv">Export CSV</Btn><Btn size="sm" variant="primary" onClick={exportPdf} data-testid="billing-export-pdf">Export PDF</Btn></div>} />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Month", "Orders", "Gross", "Collected", "Disbursed", "Pending"].map((h) => <th key={h} className="pq-label px-3 py-3 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {d.months.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No billing activity yet.</td></tr> :
              d.months.map((m) => (
                <tr key={m.month} className="border-b border-slate-100 last:border-0 text-[13px] whitespace-nowrap" data-testid={`billing-month-${m.month}`}>
                  <td className="px-3 py-3 font-semibold">{m.month ? new Date(m.month + "-01").toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—"}</td>
                  <td className="px-3 py-3">{m.orders}</td>
                  <td className="px-3 py-3 metric">{money(m.gross)}</td>
                  <td className="px-3 py-3 text-emerald-600 font-semibold">{money(m.collected)}</td>
                  <td className="px-3 py-3 text-emerald-600">{money(m.disbursed)}</td>
                  <td className="px-3 py-3 text-amber-600">{money(m.pending)}</td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </Card>
      <Card>
        <CardHead title="Recent Transactions" />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Order", "Patient", "Physiotherapist", "Amount", "Payment", "Payout", "Actions"].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {d.recent.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No transactions yet.</td></tr> :
              d.recent.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`billing-txn-${o.id}`}>
                  <td className="px-4 py-3 font-semibold">{o.ref_no}</td>
                  <td className="px-4 py-3">{o.patient_name}</td>
                  <td className="px-4 py-3">{o.physio || "—"}</td>
                  <td className="px-4 py-3"><AmountCell value={o.amount} onSave={(v) => setBilling(o.id, { amount: v }, "Amount updated")} testid={`billing-amount-${o.id}`} /></td>
                  <td className="px-4 py-3"><Badge tone={o.payment_status === "Paid" ? "emerald" : "slate"}>{o.payment_status}</Badge></td>
                  <td className="px-4 py-3"><Badge tone={o.payout_status === "Disbursed" ? "emerald" : "amber"}>{o.payout_status}</Badge></td>
                  <td className="px-4 py-3"><div className="flex gap-1.5 flex-wrap">
                    {o.payment_status !== "Paid"
                      ? <Btn size="sm" data-testid={`billing-mark-paid-${o.id}`} onClick={() => setBilling(o.id, { payment_status: "Paid" }, "Payment marked as collected")}>Mark paid</Btn>
                      : <Btn size="sm" data-testid={`billing-unpay-${o.id}`} onClick={() => setBilling(o.id, { payment_status: "Unpaid" }, "Payment reverted to unpaid")}>Undo paid</Btn>}
                    {o.payout_status !== "Disbursed"
                      ? <Btn size="sm" variant="primary" data-testid={`billing-disburse-${o.id}`} onClick={() => setBilling(o.id, { payout_status: "Disbursed" }, "Payout disbursed")}>Disburse</Btn>
                      : <Btn size="sm" data-testid={`billing-undisburse-${o.id}`} onClick={() => setBilling(o.id, { payout_status: "Pending" }, "Payout reverted to pending")}>Undo</Btn>}
                    <Btn size="sm" variant="danger" data-testid={`billing-delete-${o.id}`} onClick={() => delTxn(o)}>Delete</Btn>
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </Card>
    </>
  );
}

function AmountCell({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);
  if (!editing) return (
    <button className="metric text-[13px] hover:underline decoration-dotted" onClick={() => setEditing(true)} data-testid={testid}>
      ₹{Number(value || 0).toLocaleString("en-IN")}
    </button>
  );
  return (
    <div className="flex items-center gap-1">
      <input type="number" className="pq-input py-1 max-w-[110px] metric" value={val} autoFocus
        onChange={(e) => setVal(e.target.value)} data-testid={`${testid}-input`} />
      <Btn size="sm" variant="primary" onClick={() => { setEditing(false); onSave(val); }} data-testid={`${testid}-save`}>Save</Btn>
      <Btn size="sm" onClick={() => { setVal(value); setEditing(false); }} data-testid={`${testid}-cancel`}>×</Btn>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block mb-2"><span className="pq-label block mb-1.5">{label}</span>{children}</label>;
}
